import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Webhook, RefreshCw, PlusCircle, Copy, ShieldCheck, TrendingUp, Book, Send, Trash2 } from "lucide-react";

const ORG_TYPES = ["bank", "insurer", "government", "customs", "regulator", "fleet", "mining", "commodity", "dealer", "auction", "property", "other"];
const TIERS = ["sandbox", "starter", "growth", "enterprise", "regulator"];
const ENVS = ["sandbox", "production"];
const SCOPES = ["certificate_lookup", "trust_score", "passport_read", "escrow_read", "marketplace_read"];

export default function DeveloperPortal() {
  const { user } = useAuth();
  const canWrite = ["super_admin", "admin"].includes(user?.role);
  const [tab, setTab] = useState("clients");
  const [clients, setClients] = useState([]);
  const [subs, setSubs] = useState([]);
  const [supportedEvents, setSupportedEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [newClient, setNewClient] = useState(null);      // secret shown once
  const [newSub, setNewSub] = useState(null);            // secret shown once
  const [clientOpen, setClientOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    setBusy(true);
    try {
      const [c, w, a] = await Promise.all([
        api.get("/api-admin/clients"),
        api.get("/api-admin/webhooks"),
        api.get("/api-admin/analytics?days=7"),
      ]);
      setClients(c.data.items || []);
      setSubs(w.data.items || []);
      setSupportedEvents(w.data.supported_events || []);
      setAnalytics(a.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };

  return (
    <div>
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-6 py-10">
        <header className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="kicker">Enterprise · Developer Portal</div>
            <h1 className="display text-4xl">THOM Verify™ API Platform</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              OAuth2 client-credentials, signed webhooks, SLA-tiered rate limits and per-partner usage analytics. Sandbox and production environments. Full OpenAPI spec at <a href={`${process.env.REACT_APP_BACKEND_URL}/docs`} target="_blank" rel="noreferrer" className="underline">/docs</a>.
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={loadAll} disabled={busy}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/docs`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Book className="h-3.5 w-3.5 mr-1" /> OpenAPI docs</Button></a>
          </div>
        </header>

        {/* Analytics strip */}
        <div className="grid md:grid-cols-4 gap-4 mb-8" data-testid="devportal-analytics">
          <K icon={TrendingUp} label="API requests · 7d" value={analytics?.requests?.total} sub={`${analytics?.requests?.ok || 0} OK · ${analytics?.requests?.error || 0} err`} />
          <K icon={ShieldCheck} label="Success rate"    value={analytics?.requests?.success_pct != null ? `${analytics.requests.success_pct}%` : "—"} />
          <K icon={Webhook}   label="Webhooks delivered · 7d" value={analytics?.webhooks?.delivered} sub={`${analytics?.webhooks?.failed_pending_retry || 0} pending retry`} />
          <K icon={Trash2}    label="Webhook dead-letter" value={analytics?.webhooks?.dead_letter_recent} sub="last 7d · needs attention" />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border mb-4 gap-4">
          {[["clients","OAuth2 clients"],["webhooks","Webhooks"],["quickstart","Quickstart"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-1 pb-3 text-sm ${tab === k ? "border-b-2 border-primary text-primary font-medium" : "text-muted-foreground"}`} data-testid={`tab-${k}`}>{l}</button>
          ))}
        </div>

        {tab === "clients" && (
          <>
            {canWrite && (
              <div className="mb-3">
                <Dialog open={clientOpen} onOpenChange={setClientOpen}>
                  <DialogTrigger asChild><Button size="sm" data-testid="new-client-btn"><PlusCircle className="h-3.5 w-3.5 mr-1" /> New OAuth2 client</Button></DialogTrigger>
                  <CreateClientDialog onCreated={(r) => { setNewClient(r); setClientOpen(false); loadAll(); }} />
                </Dialog>
              </div>
            )}

            {newClient && (
              <SecretPanel title="Client secret — shown once, store it now" onClose={() => setNewClient(null)} testid="new-client-secret">
                <Row l="client_id"     v={newClient.client_id}     onCopy={() => copy(newClient.client_id)} />
                <Row l="client_secret" v={newClient.client_secret} onCopy={() => copy(newClient.client_secret)} mono danger />
                <Row l="tier / env"    v={`${newClient.tier} · ${newClient.env}`} />
                <Row l="scopes"        v={(newClient.scopes || []).join(", ")} />
                <pre className="mt-3 p-3 bg-black/85 text-emerald-200 text-[11px] rounded-sm overflow-x-auto">{`curl -X POST ${process.env.REACT_APP_BACKEND_URL}/api/oauth/token \\
  -d "grant_type=client_credentials" \\
  -d "client_id=${newClient.client_id}" \\
  -d "client_secret=${newClient.client_secret}"`}</pre>
              </SecretPanel>
            )}

            <ClientsTable rows={clients} canWrite={canWrite} onReload={loadAll} />
          </>
        )}

        {tab === "webhooks" && (
          <>
            {canWrite && (
              <div className="mb-3">
                <Dialog open={subOpen} onOpenChange={setSubOpen}>
                  <DialogTrigger asChild><Button size="sm" data-testid="new-webhook-btn"><PlusCircle className="h-3.5 w-3.5 mr-1" /> New webhook subscription</Button></DialogTrigger>
                  <CreateWebhookDialog clients={clients} supportedEvents={supportedEvents} onCreated={(r) => { setNewSub(r); setSubOpen(false); loadAll(); }} />
                </Dialog>
              </div>
            )}
            {newSub && (
              <SecretPanel title="Signing secret — shown once, store it now" onClose={() => setNewSub(null)} testid="new-sub-secret">
                <Row l="subscription id" v={newSub.id} onCopy={() => copy(newSub.id)} />
                <Row l="url"             v={newSub.url} onCopy={() => copy(newSub.url)} />
                <Row l="events"          v={(newSub.events || []).join(", ")} />
                <Row l="signing secret"  v={newSub.secret} onCopy={() => copy(newSub.secret)} mono danger />
                <p className="mt-3 text-[11.5px] text-muted-foreground">Verify inbound events with <code>X-Thom-Signature: t=&lt;epoch&gt;,v1=&lt;hex&gt;</code> and HMAC-SHA256(<code>secret</code>, <code>t + "." + body</code>).</p>
              </SecretPanel>
            )}
            <SubscriptionsTable rows={subs} canWrite={canWrite} onReload={loadAll} />
          </>
        )}

        {tab === "quickstart" && <QuickstartPanel />}
      </div>
      <Footer />
    </div>
  );
}

const K = ({ icon: Icon, label, value, sub }) => (
  <div className="border border-border bg-card p-4">
    <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-primary opacity-70" /><span className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground">{label}</span></div>
    <div className="text-3xl font-semibold mt-2 leading-none">{value ?? "—"}</div>
    {sub && <div className="text-[11.5px] text-muted-foreground mt-2">{sub}</div>}
  </div>
);

const Row = ({ l, v, mono, danger, onCopy }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-border last:border-b-0">
    <span className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground shrink-0">{l}</span>
    <div className="flex-1 min-w-0 flex items-center gap-2 justify-end">
      <span className={`truncate text-[12.5px] ${mono ? "mono" : ""} ${danger ? "text-red-700" : ""}`}>{v}</span>
      {onCopy && <button onClick={onCopy} className="text-muted-foreground hover:text-primary" title="Copy"><Copy className="h-3 w-3" /></button>}
    </div>
  </div>
);

const SecretPanel = ({ title, onClose, testid, children }) => (
  <div className="border-2 border-amber-500/60 bg-amber-500/5 p-4 mb-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div className="mono text-[10px] uppercase tracking-widest text-amber-700">{title}</div>
      <button onClick={onClose} className="text-[11px] underline text-muted-foreground">Dismiss</button>
    </div>
    {children}
  </div>
);

const ClientsTable = ({ rows, canWrite, onReload }) => {
  const revoke = async (id) => {
    if (!confirm("Revoke this client? Existing tokens remain valid until expiry (1h max).")) return;
    try { await api.post(`/api-admin/clients/${id}/revoke`); toast.success("Revoked"); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="border border-border bg-card overflow-x-auto" data-testid="clients-table">
      <table className="w-full text-[12.5px]">
        <thead className="bg-muted/40 text-left">
          <tr><th className="p-2.5">Organisation</th><th className="p-2.5">Type</th><th className="p-2.5">Env</th><th className="p-2.5">Tier</th><th className="p-2.5">Scopes</th><th className="p-2.5">Client ID</th><th className="p-2.5">Status</th><th className="p-2.5"></th></tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.client_id} data-testid={`client-row-${r.client_id}`}>
              <td className="p-2.5">{r.organisation}</td>
              <td className="p-2.5 mono text-[10.5px] uppercase">{r.org_type || "—"}</td>
              <td className="p-2.5 mono text-[10.5px] uppercase">{r.env}</td>
              <td className="p-2.5 mono text-[10.5px] uppercase">{r.tier}</td>
              <td className="p-2.5 max-w-[250px] truncate mono text-[10.5px]">{(r.scopes || []).join(", ")}</td>
              <td className="p-2.5 mono text-[10.5px] truncate max-w-[180px]">{r.client_id}</td>
              <td className="p-2.5">{r.active ? <span className="mono text-[10px] uppercase text-emerald-700">active</span> : <span className="mono text-[10px] uppercase text-red-700">revoked</span>}</td>
              <td className="p-2.5 text-right">{canWrite && r.active && <Button size="sm" variant="outline" onClick={() => revoke(r.client_id)}><Trash2 className="h-3 w-3 mr-1" /> Revoke</Button>}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground text-sm">No clients yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

const SubscriptionsTable = ({ rows, canWrite, onReload }) => {
  const rotate = async (id) => {
    if (!confirm("Rotate signing secret? Old signature will stop working immediately.")) return;
    try { const r = await api.post(`/api-admin/webhooks/${id}/rotate-secret`); toast.success("Rotated"); alert("New secret: " + r.data.secret); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deactivate = async (id) => {
    if (!confirm("Deactivate subscription?")) return;
    try { await api.post(`/api-admin/webhooks/${id}/deactivate`); toast.success("Deactivated"); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const test = async (id) => {
    try { const r = await api.post(`/api-admin/webhooks/${id}/test`); toast.success(`Test enqueued (${r.data.enqueued} delivery)`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="border border-border bg-card overflow-x-auto" data-testid="subs-table">
      <table className="w-full text-[12.5px]">
        <thead className="bg-muted/40 text-left">
          <tr><th className="p-2.5">Organisation</th><th className="p-2.5">URL</th><th className="p-2.5">Env</th><th className="p-2.5">Events</th><th className="p-2.5">Last success / failure</th><th className="p-2.5">Streak</th><th className="p-2.5"></th></tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.id} data-testid={`sub-row-${r.id}`}>
              <td className="p-2.5">{r.organisation}</td>
              <td className="p-2.5 max-w-[260px] truncate mono text-[10.5px]">{r.url}</td>
              <td className="p-2.5 mono text-[10.5px] uppercase">{r.env}</td>
              <td className="p-2.5 max-w-[220px] mono text-[10px]">{(r.events || []).join(", ")}</td>
              <td className="p-2.5 mono text-[10px] text-muted-foreground">{r.last_success_at ? `✓ ${r.last_success_at.slice(0,16).replace('T',' ')}` : "—"}<br />{r.last_failure_at ? `✗ ${r.last_failure_at.slice(0,16).replace('T',' ')}` : "—"}</td>
              <td className="p-2.5"><span className={`mono text-[10px] uppercase ${r.failure_streak >= 3 ? "text-red-700" : "text-emerald-700"}`}>{r.failure_streak || 0}</span></td>
              <td className="p-2.5 text-right whitespace-nowrap">
                {canWrite && r.active && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => test(r.id)} title="Test"><Send className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => rotate(r.id)} title="Rotate secret"><RefreshCw className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => deactivate(r.id)} title="Deactivate"><Trash2 className="h-3 w-3" /></Button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground text-sm">No subscriptions yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

const CreateClientDialog = ({ onCreated }) => {
  const [f, setF] = useState({ organisation: "", org_type: "bank", scopes: ["certificate_lookup", "trust_score"], tier: "starter", env: "sandbox", description: "" });
  const submit = async () => {
    if (!f.organisation) return toast.error("Organisation required");
    try { const r = await api.post("/api-admin/clients", f); onCreated(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleScope = (s) => setF({ ...f, scopes: f.scopes.includes(s) ? f.scopes.filter(x => x !== s) : [...f.scopes, s] });
  return (
    <DialogContent className="max-w-lg" data-testid="new-client-dialog">
      <DialogHeader><DialogTitle>New OAuth2 client</DialogTitle><DialogDescription>Server-to-server credentials for a partner organisation. Client secret is shown once — store it in the partner's vault immediately.</DialogDescription></DialogHeader>
      <div className="grid grid-cols-2 gap-3 mt-2">
        <F label="Organisation" span={2}><Input value={f.organisation} onChange={(e) => setF({ ...f, organisation: e.target.value })} className="h-8 text-[12.5px]" data-testid="cli-org" /></F>
        <F label="Type"><select className="h-8 w-full border border-border bg-background text-[12.5px] px-2 rounded-sm" value={f.org_type} onChange={(e) => setF({ ...f, org_type: e.target.value })}>{ORG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></F>
        <F label="Tier"><select className="h-8 w-full border border-border bg-background text-[12.5px] px-2 rounded-sm" value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })}>{TIERS.map(t => <option key={t} value={t}>{t}</option>)}</select></F>
        <F label="Env" span={2}><select className="h-8 w-full border border-border bg-background text-[12.5px] px-2 rounded-sm" value={f.env} onChange={(e) => setF({ ...f, env: e.target.value })}>{ENVS.map(e => <option key={e} value={e}>{e}</option>)}</select></F>
        <F label="Scopes" span={2}>
          <div className="flex gap-2 flex-wrap">{SCOPES.map(s => <button type="button" key={s} onClick={() => toggleScope(s)} className={`mono text-[10.5px] uppercase tracking-widest px-2 py-1 rounded-sm border ${f.scopes.includes(s) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>{s}</button>)}</div>
        </F>
        <F label="Description" span={2}><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="text-[12.5px]" /></F>
      </div>
      <DialogFooter><Button onClick={submit} data-testid="cli-submit">Create</Button></DialogFooter>
    </DialogContent>
  );
};

const CreateWebhookDialog = ({ clients, supportedEvents, onCreated }) => {
  const [f, setF] = useState({ client_id: "", url: "", events: [], description: "" });
  const submit = async () => {
    if (!f.client_id || !f.url || f.events.length === 0) return toast.error("client, URL and events are required");
    try { const r = await api.post("/api-admin/webhooks", f); onCreated(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleEvent = (ev) => setF({ ...f, events: f.events.includes(ev) ? f.events.filter(x => x !== ev) : [...f.events, ev] });
  return (
    <DialogContent className="max-w-lg" data-testid="new-webhook-dialog">
      <DialogHeader><DialogTitle>New webhook subscription</DialogTitle><DialogDescription>Signed HMAC-SHA256 events. Signing secret is shown once. Retries with exponential backoff (1m→12h) over 6 attempts before dead-letter.</DialogDescription></DialogHeader>
      <div className="grid grid-cols-2 gap-3 mt-2">
        <F label="Client" span={2}>
          <select className="h-8 w-full border border-border bg-background text-[12.5px] px-2 rounded-sm" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} data-testid="wh-client">
            <option value="">— select —</option>
            {clients.filter(c => c.active).map(c => <option key={c.client_id} value={c.client_id}>{c.organisation} · {c.client_id} · {c.env}</option>)}
          </select>
        </F>
        <F label="Destination URL" span={2}><Input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://partner.example.com/webhooks/thom" className="h-8 text-[12.5px]" data-testid="wh-url" /></F>
        <F label="Events" span={2}>
          <div className="flex gap-2 flex-wrap">{supportedEvents.map(ev => <button type="button" key={ev} onClick={() => toggleEvent(ev)} className={`mono text-[10.5px] tracking-widest px-2 py-1 rounded-sm border ${f.events.includes(ev) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>{ev}</button>)}</div>
        </F>
        <F label="Description" span={2}><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="text-[12.5px]" /></F>
      </div>
      <DialogFooter><Button onClick={submit} data-testid="wh-submit">Create subscription</Button></DialogFooter>
    </DialogContent>
  );
};

const F = ({ label, span, children }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
    {children}
  </div>
);

const QuickstartPanel = () => (
  <div className="prose max-w-3xl">
    <h3 className="text-lg font-semibold">Quickstart</h3>
    <ol className="list-decimal ml-5 text-[13.5px] space-y-3 mt-3">
      <li><strong>Create a sandbox OAuth2 client</strong> (Clients tab → New OAuth2 client). Copy the <code>client_id</code> + <code>client_secret</code> — the secret is shown once.</li>
      <li><strong>Exchange credentials for an access token</strong>:
        <pre className="mt-2 p-3 bg-black/85 text-emerald-200 text-[11px] rounded-sm overflow-x-auto">{`curl -X POST ${process.env.REACT_APP_BACKEND_URL}/api/oauth/token \\
  -d "grant_type=client_credentials" \\
  -d "client_id=<CLIENT_ID>" \\
  -d "client_secret=<CLIENT_SECRET>"`}</pre>
      </li>
      <li><strong>Call an enterprise endpoint</strong> — tokens expire after 1 hour:
        <pre className="mt-2 p-3 bg-black/85 text-emerald-200 text-[11px] rounded-sm overflow-x-auto">{`curl ${process.env.REACT_APP_BACKEND_URL}/api/enterprise/v1/certificate/TVC-A1B2 \\
  -H "Authorization: Bearer <ACCESS_TOKEN>"`}</pre>
      </li>
      <li><strong>Subscribe to webhooks</strong> — subscribe your endpoint to events like <code>certificate.issued</code>, <code>escrow.released</code>. Store the signing secret and verify inbound requests using the <code>X-Thom-Signature</code> header.</li>
      <li><strong>Introspect a token</strong> at <code>POST /api/oauth/introspect</code> with <code>token=&lt;jwt&gt;</code> for RFC-7662 claims.</li>
    </ol>
    <h4 className="mt-6 font-semibold">Signature verification (any language)</h4>
    <pre className="mt-2 p-3 bg-black/85 text-emerald-200 text-[11px] rounded-sm overflow-x-auto">{`# X-Thom-Signature: t=1712345678,v1=abcdef...
parts = dict(kv.split("=", 1) for kv in header.split(","))
expected = hmac.new(secret.encode(),
                    f"{parts['t']}.{body}".encode(),
                    hashlib.sha256).hexdigest()
assert hmac.compare_digest(expected, parts["v1"])`}</pre>
    <h4 className="mt-6 font-semibold">Rate limits (per client, per minute)</h4>
    <ul className="list-disc ml-5 text-[13px]">
      <li><code>sandbox</code> — 30/min · 5 000/day</li>
      <li><code>starter</code> — 60/min · 20 000/day</li>
      <li><code>growth</code> — 300/min · 100 000/day</li>
      <li><code>enterprise</code> — 1 200/min · 1 000 000/day</li>
      <li><code>regulator</code> — 6 000/min · 5 000 000/day</li>
    </ul>
  </div>
);
